import React, {useCallback, useEffect} from 'react';
import {useSelector} from 'react-redux';

import uniqueId from 'lodash.uniqueid';
import {RESPONSE_NOTE_LIFETIME} from 'src/contexts/background-task-context/background-task-context-provider';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {
    getMcpContracts,
    updateMcpContract,
    updateMcpContractCredits,
} from 'src/openapi/admin/apis/McpContractsApi';
import {useAppDispatch} from 'src/store/hooks';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

import {
    selectMcpContracts,
    selectMcpContractsLoading,
    selectMcpSaving,
} from '../../services/mcp-slice';

import {PricingView} from './pricing-view';

import type {McpUpdateContractRequest} from 'src/openapi/admin/models/McpUpdateContractRequest';
import type {McpUpdateCreditsRequest} from 'src/openapi/admin/models/McpUpdateCreditsRequest';

type Props = {
    accountId: number,
};

export const PricingController = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();
    const {canEditAccountMcp} = useAdminAccessRight();
    const canEdit = canEditAccountMcp(accountId);

    const contracts = useSelector(selectMcpContracts);
    const contractsLoading = useSelector(selectMcpContractsLoading);
    const saving = useSelector(selectMcpSaving);

    useEffect(() => {
        apiDispatch(getMcpContracts({accountId}));
    }, [accountId, apiDispatch]);

    const onReloadContracts = useCallback(() => {
        apiDispatch(getMcpContracts({accountId}));
    }, [accountId, apiDispatch]);

    const onUpdateContract = useCallback(async (contractId: number, data: McpUpdateContractRequest) => {
        const response = await apiDispatch(updateMcpContract({contractId, mcpUpdateContractRequest: data}));
        if (response.payload.error) {
            onCreateNote({
                id: uniqueId('mcp-contract-update-error-'),
                status: Status.ERROR,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () => response.payload.data?.message || 'Failed to update contract',
            });
        } else {
            onCreateNote({
                id: uniqueId('mcp-contract-update-'),
                status: Status.SUCCESS,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () => 'Contract has been successfully updated',
            });
        }
    }, [apiDispatch, onCreateNote]);

    const onUpdateCredits = useCallback(async (contractId: number, data: McpUpdateCreditsRequest) => {
        const response = await apiDispatch(updateMcpContractCredits({contractId, mcpUpdateCreditsRequest: data}));
        if (response.payload.error) {
            onCreateNote({
                id: uniqueId('mcp-credits-update-error-'),
                status: Status.ERROR,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () => response.payload.data?.message || 'Failed to update contract credits',
            });
        } else {
            onReloadContracts();
            onCreateNote({
                id: uniqueId('mcp-credits-update-'),
                status: Status.SUCCESS,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () => 'Contract has been successfully updated',
            });
        }
    }, [apiDispatch, onReloadContracts, onCreateNote]);

    return (
        <PricingView
            accountId={accountId}
            contracts={contracts}
            contractsLoading={contractsLoading}
            saving={saving}
            canEdit={canEdit}
            onReloadContracts={onReloadContracts}
            onUpdateContract={onUpdateContract}
            onUpdateCredits={onUpdateCredits}
        />
    );
};
